import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { FGStorefrontGraphQL } from "@fgtheme/graphql";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class WICartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #abortController = new AbortController();
  #isRunningOnCartUpdate = false;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (this.#isRunningOnCartUpdate) {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
    this.#isRunningOnCartUpdate = true;
    let itemCount = 0;
    try {
      const cartResponse = await fetch('/cart.js', { signal: this.#abortController.signal })
      const cartResult = await cartResponse.json();
      const formattedIds = cartResult.items.map((/** @type {any} */ item) => `gid://shopify/ProductVariant/${item.variant_id}`);
      const uniqIds = [...new Set(formattedIds)];
      const payload = {
        query: `
          query ProductVariantNodes($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on ProductVariant {
                id
                metafield_bottle_quantity: metafield(namespace: "fgw", key: "bottle_quantity") {
                  value
                }
              }
            }
          }
        `,
        variables: { ids: uniqIds }
      };
      const nodesResponse = await FGStorefrontGraphQL(payload, this.#abortController);
      const nodesResult = await nodesResponse.json();
      cartResult.items.forEach((/** @type {any} */ item) => {
        const matchingNode = nodesResult.data.nodes.find((/** @type {any} */ node) => `gid://shopify/ProductVariant/${item.variant_id}` === node.id);
        if (matchingNode && matchingNode.metafield_bottle_quantity && matchingNode.metafield_bottle_quantity.value) {
          const intValue = parseInt(matchingNode.metafield_bottle_quantity.value, 10);
          // For some reason this isn't a value >= 1, add the quantity of the item to the cart count
          if (intValue < 1) {
            itemCount = itemCount + item.quantity;
          } else {
            const totalBottles = intValue * item.quantity;
            itemCount = itemCount + totalBottles;
          }
        } else {
          // we don't have a value coming back for the metafield at all, add the quantity of the line item to the count
          itemCount = itemCount + item.quantity;
        }
      });
    } catch (e) {
      console.warn('Cart Icon Update Aborted', e);
      return;
    }
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
    this.#isRunningOnCartUpdate = false;
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

customElements.define('cart-icon', WICartIcon);
